import { createFileRoute } from "@tanstack/react-router";
import {
  Award,
  BarChart3,
  CheckCircle2,
  Clock3,
  PieChart,
  ShieldAlert,
  ShieldCheck,
  Trophy,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge, SectionCard, UserLink } from "../../../../../components/ui";
import { trpc } from "../../../../../lib/trpc";
import { cn } from "../../../../../lib/utils";

export const Route = createFileRoute("/app/clubs/$slug/admin/analytics")({
  component: ClubAdminAnalyticsPage,
});

function ClubAdminAnalyticsPage() {
  const { slug } = Route.useParams();
  const { data: club } = trpc.club.get.useQuery({ slug });
  const analyticsQuery = trpc.dispute.analytics.useQuery(
    { clubId: club?.id ?? "" },
    { enabled: !!club?.id },
  );

  if (analyticsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <SectionCard icon={<BarChart3 className="size-4" />} title="Dispute Analytics">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-[88px] rounded-xl bg-surface-800/40 animate-pulse" />
            ))}
          </div>
        </SectionCard>
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard icon={<PieChart className="size-4" />} title="Resolution Mix">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-1">
                  <div className="h-4 w-1/2 rounded bg-surface-800/40 animate-pulse" />
                  <div className="h-2 rounded-full bg-surface-800/40 animate-pulse" />
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard icon={<PieChart className="size-4" />} title="Category Mix">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-1">
                  <div className="h-4 w-1/2 rounded bg-surface-800/40 animate-pulse" />
                  <div className="h-2 rounded-full bg-surface-800/40 animate-pulse" />
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard icon={<Award className="size-4" />} title="Top Claimers">
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          </SectionCard>
          <SectionCard icon={<Award className="size-4" />} title="Top Resolvers">
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg bg-surface-800/40 animate-pulse" />
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  if (analyticsQuery.error || !analyticsQuery.data) {
    return (
      <SectionCard title="Dispute Analytics">
        <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-4 text-sm text-error-300">
          {analyticsQuery.error?.message || "Failed to load dispute analytics."}
        </div>
      </SectionCard>
    );
  }

  const data = analyticsQuery.data;
  const unresolvedCoverage =
    data.unresolvedDisputes > 0
      ? Math.round((data.unresolvedClaimed / data.unresolvedDisputes) * 100)
      : 100;

  return (
    <div className="space-y-4">
      <SectionCard icon={<BarChart3 className="size-4" />} title="Dispute Analytics">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Total Disputes"
            value={data.totalDisputes}
            hint="All-time club disputes"
            icon={<ShieldAlert className="size-4 text-brand-300" />}
          />
          <MetricCard
            label="Unresolved"
            value={data.unresolvedDisputes}
            hint={`${data.unresolvedClaimed} claimed · ${data.unresolvedUnclaimed} unclaimed`}
            icon={<Clock3 className="size-4 text-yellow-300" />}
            extra={
              data.unresolvedDisputes > 0 ? (
                <div className="mt-2 flex items-center gap-1.5">
                  <div className="h-1.5 flex-1 rounded-full bg-surface-800/60 overflow-hidden flex">
                    <div
                      className="h-full rounded-full bg-blue-400/80"
                      style={{
                        width: `${Math.round((data.unresolvedClaimed / data.unresolvedDisputes) * 100)}%`,
                      }}
                    />
                    <div
                      className="h-full bg-yellow-400/60"
                      style={{
                        width: `${Math.round((data.unresolvedUnclaimed / data.unresolvedDisputes) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ) : undefined
            }
          />
          <MetricCard
            label="Resolved"
            value={data.resolvedDisputes}
            hint={`${data.correctionCount} corrected after initial resolution`}
            icon={<CheckCircle2 className="size-4 text-green-300" />}
          />
          <MetricCard
            label="Claim Coverage"
            value={`${unresolvedCoverage}%`}
            hint="Unresolved disputes with active claim"
            icon={<ShieldCheck className="size-4 text-brand-300" />}
          />
        </div>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard icon={<PieChart className="size-4" />} title="Resolution Mix">
          <DistributionList
            items={data.byResolution.map((item) => ({
              key: item.resolution,
              label: formatResolution(item.resolution),
              count: item.count,
              color:
                item.resolution === "upheld"
                  ? "green"
                  : item.resolution === "overturned"
                    ? "blue"
                    : "red",
            }))}
            emptyLabel="No resolved disputes yet."
          />
        </SectionCard>

        <SectionCard icon={<PieChart className="size-4" />} title="Category Mix">
          <DistributionList
            items={data.byCategory.map((item) => ({
              key: item.category,
              label: formatCategory(item.category),
              count: item.count,
              color: categoryColor(item.category),
            }))}
            emptyLabel="No disputes filed yet."
          />
        </SectionCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard icon={<Award className="size-4" />} title="Top Claimers">
          <LeaderboardList
            items={data.topClaimers}
            countLabel="claims"
            emptyLabel="No claims yet."
          />
        </SectionCard>
        <SectionCard icon={<Award className="size-4" />} title="Top Resolvers">
          <LeaderboardList
            items={data.topResolvers}
            countLabel="resolved"
            emptyLabel="No resolutions yet."
          />
        </SectionCard>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  icon,
  extra,
}: {
  label: string;
  value: number | string;
  hint: string;
  icon: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-surface-800/60 bg-surface-850/70 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wider text-surface-500">{label}</p>
        {icon}
      </div>
      <p className="mt-1 text-2xl font-display font-bold text-white tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-surface-500">{hint}</p>
      {extra}
    </div>
  );
}

function categoryColor(value: string): "gray" | "blue" | "green" | "red" | "yellow" | "purple" {
  switch (value) {
    case "wrong_result":
      return "yellow";
    case "cheating":
      return "red";
    case "abandonment":
      return "purple";
    case "technical":
      return "blue";
    default:
      return "gray";
  }
}

function DistributionList({
  items,
  emptyLabel,
}: {
  items: Array<{
    key: string;
    label: string;
    count: number;
    color: "gray" | "blue" | "green" | "red" | "yellow" | "purple";
  }>;
  emptyLabel: string;
}) {
  const total = items.reduce((sum, item) => sum + item.count, 0);

  if (items.length === 0 || total === 0) {
    return <p className="text-sm text-surface-400">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-2">
      {items
        .sort((a, b) => b.count - a.count)
        .map((item) => {
          const percent = Math.round((item.count / total) * 100);
          const barWidth = Math.max(4, percent);
          return (
            <div key={item.key} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-surface-200">{item.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-surface-500 tabular-nums">{percent}%</span>
                  <Badge variant="subtle" color={item.color}>
                    {item.count}
                  </Badge>
                </div>
              </div>
              <div className="h-2 rounded-full bg-surface-800/60 overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full",
                    item.color === "green"
                      ? "bg-green-400/80"
                      : item.color === "blue"
                        ? "bg-blue-400/80"
                        : item.color === "red"
                          ? "bg-error-400/80"
                          : item.color === "yellow"
                            ? "bg-yellow-400/80"
                            : item.color === "purple"
                              ? "bg-purple-400/80"
                              : "bg-surface-400/70",
                  )}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
}

function rankColor(index: number) {
  if (index === 0) return "text-yellow-400";
  if (index === 1) return "text-surface-300";
  if (index === 2) return "text-amber-600";
  return "text-surface-500";
}

function LeaderboardList({
  items,
  countLabel,
  emptyLabel,
}: {
  items: Array<{ userId: string; username: string | null; slug?: string | null; count: number }>;
  countLabel: string;
  emptyLabel: string;
}) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="flex items-center justify-center size-10 rounded-xl bg-surface-800 border border-surface-700/50 mb-3">
          <Trophy className="size-5 text-surface-500" />
        </div>
        <p className="text-sm font-medium text-surface-300">No activity yet</p>
        <p className="mt-1 text-xs text-surface-500">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div
          key={item.userId}
          className="flex items-center justify-between rounded-lg border border-surface-800/60 bg-surface-900/30 px-3 py-2"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={cn(
                "w-5 text-right font-display font-bold text-sm shrink-0",
                rankColor(idx),
              )}
            >
              {idx + 1}
            </span>
            {item.username ? (
              <UserLink
                slug={item.slug ?? item.userId}
                className="text-sm text-surface-100 truncate hover:text-brand-400 transition-colors"
              >
                {item.username}
              </UserLink>
            ) : (
              <span className="text-sm text-surface-400 truncate">{item.userId}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-surface-500">{countLabel}</span>
            <Badge variant="subtle" color="blue">
              {item.count}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatResolution(value: string) {
  switch (value) {
    case "upheld":
      return "Upheld";
    case "overturned":
      return "Overturned";
    case "cancelled":
      return "Cancelled";
    default:
      return value;
  }
}

function formatCategory(value: string) {
  switch (value) {
    case "wrong_result":
      return "Wrong Result";
    case "cheating":
      return "Cheating";
    case "abandonment":
      return "Abandonment";
    case "technical":
      return "Technical";
    case "other":
      return "Other";
    default:
      return value;
  }
}
